import { useLatestBuild } from '@/hooks/useLatestBuild'
import { useResolve } from '@/hooks/useResolve'
import { DOWNLOAD_ANCHOR, MAC_BUILD } from '@/lib/landing'
import { ButtonLink, DownloadIcon, GenieMark } from './primitives'
import { ProductFrame } from './ProductFrame'
import { ModeSwitcher, useModeCycle } from './studio/ModeSwitcher'

/**
 * Hero: centred head in the Magic Lamp shape (mark, uppercase display, mono facts,
 * two actions, a copyable command), then the studio still on a dark grainy plate.
 */
export function Hero() {
  const build = useLatestBuild()
  const [mode, pick] = useModeCycle()
  const title = useResolve<HTMLHeadingElement>()

  return (
    <section>
      <div className="landing-shell flex flex-col items-center pt-20 pb-14 text-center md:pt-24">
        <GenieMark className="size-12 text-ink" />
        <h1 ref={title.ref} data-grade="100" onMouseEnter={title.replay} className="display mt-8 text-[clamp(36px,5.8vw,88px)]">
          A{' '}
          <span className="relative inline-block">
            genie
            <span
              aria-hidden
              className="absolute -top-[0.62em] -right-[0.55em] rotate-[9deg] rounded-[3px] bg-lamp px-2.5 py-1.5 font-mono text-[clamp(10px,1.1vw,16px)] leading-none tracking-[0.04em] text-ink shadow-[0_2px_0_rgba(2,2,2,0.9)]"
            >
              BETA
            </span>
          </span>{' '}
          in
          <br />
          your studio
        </h1>
        <p className="mono-label mt-5 text-ink-3">Agentic creative studio · open models on your Mac · cloud when you want it</p>
        <p className="mt-6 max-w-xl text-balance text-[16px] leading-relaxed text-ink-2">
          Tell it what you want. It plans the work, runs open models on your Mac, and lands every result on a canvas you
          keep editing. <span className="text-ink">Image, video, music and 3D</span>, local first, with cloud models a
          click away.
        </p>
        <div className="mt-8 flex flex-wrap justify-center gap-2">
          <ButtonLink href={DOWNLOAD_ANCHOR}>
            <DownloadIcon />
            Download for macOS
          </ButtonLink>
          <ButtonLink href="#how" variant="outline">
            See a session <span aria-hidden>↓</span>
          </ButtonLink>
        </div>
        <p className="mono-label mt-4 text-ink-3">
          <span className="num">{build.size}</span> · {MAC_BUILD.requirements} · Free forever
        </p>
      </div>

      <figure className="relative">
        {/* No plate: one soft amber glow behind the still, fading into the page on every side. */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 -top-40 -bottom-24 bg-[radial-gradient(ellipse_78%_68%_at_50%_48%,rgba(242,165,22,0.24),rgba(242,165,22,0.1)_50%,transparent_80%)]"
        />
        <div className="landing-shell relative pt-4">
          <div className="mx-auto w-full max-w-[1100px] overflow-hidden rounded-[6px] border border-rule shadow-[0_24px_60px_rgba(2,2,2,0.12)]">
            <ProductFrame mode={mode} />
          </div>
          <figcaption className="mono-label mx-auto mt-4 flex max-w-[1100px] justify-between gap-4 text-ink-3">
            <span>The studio window</span>
            <span className="hidden sm:inline">Three modes, one session</span>
          </figcaption>
          <div className="mt-5">
            <ModeSwitcher mode={mode} onPick={pick} />
          </div>
        </div>
      </figure>
    </section>
  )
}
